/**
 * Custom (from-scratch) criteria editor: units → Demonstrate / Explain rows.
 * Each criterion row has its own element number and optional title.
 */
import React, { useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { ClipboardList, Plus, Trash2 } from "../icons";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { TRAIN_DRIVER_V1_CYCLE } from "../cycles/trainDriverV1";
import { cb, cbCn } from "./cycleBuilderTheme";
import { IndustryUnitPickerDialog, wizardItemLineKey } from "./IndustryUnitPickerDialog";
import type { WizardCriteriaItem } from "./CycleWizardStore";
import {
  appendIndustryUnitsToWizardItems,
  composeCustomUnitNo,
  customUnitGroupKey,
  groupWizardCriteriaByUnit,
  nextCustomElementNo,
  nextCustomUnitNumber,
  composeWizardUnitNo,
  type LibraryUnit,
  type ScratchUnitGroup,
} from "./cycleLibraryUi";

function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(inputs));
}

function newClientKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function blankRow(partial: Partial<WizardCriteriaItem> & Pick<WizardCriteriaItem, "type">): WizardCriteriaItem {
  return {
    unit_no: "",
    element_no: "",
    element: "",
    criteria: "",
    status: "Competent",
    wizardSource: "custom",
    clientKey: newClientKey(),
    ...partial,
  };
}

function indicesForKind(unit: ScratchUnitGroup, kind: "demonstrate" | "explain"): number[] {
  return unit.elements.flatMap((el) => (kind === "demonstrate" ? el.demonstrate : el.explain));
}

export function CustomCycleCriteriaEditor({
  items,
  onChange,
  demoLabel,
  explainLabel,
  isDarkMode = false,
  isMobile,
}: {
  items: WizardCriteriaItem[];
  onChange: (items: WizardCriteriaItem[]) => void;
  demoLabel: string;
  explainLabel: string;
  isDarkMode?: boolean;
  isMobile?: boolean;
}) {
  const units = useMemo(() => groupWizardCriteriaByUnit(items), [items]);
  const [industryPickerOpen, setIndustryPickerOpen] = useState(false);

  const setItems = (next: WizardCriteriaItem[]) => onChange(next);

  const updateAt = (index: number, patch: Partial<WizardCriteriaItem>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeAt = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const addUnit = () => {
    const number = nextCustomUnitNumber(items);
    const unitNo = composeCustomUnitNo(number, "");
    setItems([
      ...items,
      blankRow({
        unit_no: unitNo,
        element_no: `${number}.1`,
        type: "demonstrate",
      }),
    ]);
  };

  const addIndustryUnits = (picked: LibraryUnit[]) => {
    const added = appendIndustryUnitsToWizardItems(items, picked).map((row) => ({
      ...row,
      clientKey: newClientKey(),
    }));
    if (added.length === 0) return;
    setItems([...items, ...added]);
  };

  const patchUnit = (unitKey: string, number: string, name: string) => {
    const sample = items.find((item) => customUnitGroupKey(item.unit_no) === unitKey);
    const nextUnitNo = composeWizardUnitNo(number, name, sample?.unit_no);
    setItems(
      items.map((item) =>
        customUnitGroupKey(item.unit_no) === unitKey ? { ...item, unit_no: nextUnitNo } : item
      )
    );
  };

  const deleteUnit = (unitKey: string) => {
    setItems(items.filter((item) => customUnitGroupKey(item.unit_no) !== unitKey));
  };

  const addCriterion = (unit: ScratchUnitGroup, type: "demonstrate" | "explain") => {
    const unitNo = composeWizardUnitNo(unit.unitNumber, unit.unitName, unit.unitNo);
    setItems([
      ...items,
      blankRow({
        unit_no: unitNo,
        element_no: nextCustomElementNo(items, unit.unitKey),
        element: "",
        type,
      }),
    ]);
  };

  const renderRow = (idx: number) => {
    const row = items[idx];
    if (!row) return null;
    return (
      <div
        key={row.clientKey ?? `c-${idx}`}
        className={cbCn(
          "w-full min-w-0 rounded-lg border p-2.5 sm:p-3",
          isDarkMode ? "border-slate-600 bg-slate-900/50" : "border-slate-200 bg-white"
        )}
      >
        <div
          className={cn(
            "grid w-full min-w-0 items-end gap-2",
            isMobile ? "grid-cols-1" : "grid-cols-[6.5rem_minmax(12rem,1fr)_auto]"
          )}
        >
          <label className="min-w-0">
            <span className={cbCn("mb-1 block text-[11px] font-medium uppercase tracking-wide", cb.bodySm(isDarkMode))}>
              {translateUi("Element no.")}
            </span>
            <input
              type="text"
              value={row.element_no}
              onChange={(e) => updateAt(idx, { element_no: e.target.value })}
              placeholder="1.1"
              className={cbCn("w-full min-w-0 rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
            />
          </label>
          <label className="min-w-0">
            <span className={cbCn("mb-1 block text-[11px] font-medium uppercase tracking-wide", cb.bodySm(isDarkMode))}>
              {translateUi("Element title")} ({translateUi("optional")})
            </span>
            <input
              type="text"
              value={row.element}
              onChange={(e) => updateAt(idx, { element: e.target.value })}
              placeholder={translateUi("Element Title - Optional")}
              className={cbCn("w-full min-w-0 rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
            />
          </label>
          <button
            type="button"
            onClick={() => removeAt(idx)}
            className={cbCn(
              "self-end rounded p-2",
              isMobile && "min-h-[44px] w-full justify-self-stretch",
              isDarkMode ? "text-red-300 hover:bg-red-950/50" : "text-red-600 hover:bg-red-100"
            )}
            title={translateUi("Delete")}
          >
            <Trash2 className="mx-auto h-4 w-4" />
          </button>
        </div>
        <textarea
          value={row.criteria}
          onChange={(e) => updateAt(idx, { criteria: e.target.value })}
          placeholder={translateUi("Criteria text")}
          rows={2}
          className={cbCn(
            "mt-2 w-full min-w-0 resize-y rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1",
            cb.input(isDarkMode)
          )}
        />
      </div>
    );
  };

  return (
    <div className="w-full min-w-0 space-y-4">
      {units.length === 0 ? (
        <div
          className={cn(
            "rounded-xl border-2 border-dashed px-4 py-10 text-center",
            isDarkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-slate-50/70"
          )}
        >
          <p className={cbCn("text-sm", cb.body(isDarkMode))}>
            {translateUi(
              "Add a unit, then add Demonstrate and Explain rows. Each row has its own element number and optional title."
            )}
          </p>
          <div className={cn("mt-4 flex flex-wrap items-center justify-center gap-2", isMobile && "flex-col")}>
            <button
              type="button"
              onClick={() => setIndustryPickerOpen(true)}
              className={cn("inline-flex items-center gap-2", cb.accentBtn(isDarkMode, "indigo"), isMobile && "min-h-[44px] w-full justify-center")}
            >
              <ClipboardList className="h-4 w-4" />
              {translateUi("Add industry standard units")}
            </button>
            <button
              type="button"
              onClick={addUnit}
              className={cn("inline-flex items-center gap-2", cb.accentBtn(isDarkMode, "indigo"), isMobile && "min-h-[44px] w-full justify-center")}
            >
              <Plus className="h-4 w-4" />
              {translateUi("Add blank unit")}
            </button>
          </div>
        </div>
      ) : (
        units.map((unit) => {
          const unitStableKey =
            unit.elements[0]?.demonstrate[0] ?? unit.elements[0]?.explain[0] ?? unit.unitKey;
          return (
            <section
              key={`unit-${unitStableKey}`}
              className={cbCn("w-full min-w-0 rounded-xl border p-3 sm:p-4", isDarkMode ? "border-slate-600 bg-slate-800/50" : "border-slate-200 bg-white")}
            >
              <div className={cn("flex gap-3", isMobile ? "flex-col" : "flex-wrap items-end justify-between")}>
                <div className={cn("grid min-w-0 flex-1 gap-3", isMobile ? "grid-cols-1" : "sm:grid-cols-[7rem_minmax(0,1fr)]")}>
                  <label className="min-w-0">
                    <span className={cbCn("mb-1 block text-xs font-semibold uppercase tracking-wide", cb.bodySm(isDarkMode))}>
                      {translateUi("Unit no.")}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={unit.unitNumber}
                      onChange={(e) => patchUnit(unit.unitKey, e.target.value.replace(/[^\d]/g, ""), unit.unitName)}
                      placeholder="1"
                      className={cbCn("w-full rounded-lg border px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-1", cb.input(isDarkMode))}
                    />
                  </label>
                  <label className="min-w-0">
                    <span className={cbCn("mb-1 block text-xs font-semibold uppercase tracking-wide", cb.bodySm(isDarkMode))}>
                      {translateUi("Unit name")}
                    </span>
                    <input
                      type="text"
                      value={unit.unitName}
                      onChange={(e) => patchUnit(unit.unitKey, unit.unitNumber, e.target.value)}
                      placeholder={translateUi("e.g. Prepare for duty")}
                      className={cbCn("w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1", cb.input(isDarkMode))}
                    />
                  </label>
                </div>
                <button type="button" onClick={() => deleteUnit(unit.unitKey)} className={cb.dangerBtn(isDarkMode)}>
                  {translateUi("Delete unit")}
                </button>
              </div>

              <div className={cn("mt-4 grid w-full min-w-0 items-stretch gap-5", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                {(["demonstrate", "explain"] as const).map((kind) => {
                  const indices = indicesForKind(unit, kind);
                  const accent = kind === "demonstrate" ? "emerald" : "amber";
                  const title = kind === "demonstrate" ? demoLabel : explainLabel;
                  return (
                    <div
                      key={kind}
                      className={cbCn(
                        "flex min-w-0 flex-col rounded-xl border-2 border-dashed p-3",
                        kind === "demonstrate"
                          ? isDarkMode
                            ? "border-emerald-700/50 bg-emerald-950/20"
                            : "border-emerald-200 bg-emerald-50/40"
                          : isDarkMode
                            ? "border-amber-700/50 bg-amber-950/20"
                            : "border-amber-200 bg-amber-50/40"
                      )}
                    >
                      <p
                        className={cbCn(
                          "mb-3 text-xs font-semibold uppercase tracking-wide",
                          kind === "demonstrate"
                            ? isDarkMode
                              ? "text-emerald-300"
                              : "text-emerald-700"
                            : isDarkMode
                              ? "text-amber-300"
                              : "text-amber-700"
                        )}
                      >
                        {title}
                        <span className={cbCn("ml-2 font-medium normal-case tracking-normal", cb.bodySm(isDarkMode))}>
                          {indices.length}
                        </span>
                      </p>
                      <div className="flex min-w-0 flex-1 flex-col gap-3">
                        {indices.length === 0 ? (
                          <button
                            type="button"
                            onClick={() => addCriterion(unit, kind)}
                            className={cn(
                              "flex min-h-[7.5rem] w-full flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-6 text-sm font-medium",
                              cb.accentBtn(isDarkMode, accent),
                              isMobile && "min-h-[88px]"
                            )}
                          >
                            <Plus className="h-4 w-4 shrink-0" />
                            {translateUi("Add {{label}} criterion", { label: title })}
                          </button>
                        ) : (
                          <>
                            {indices.map((idx) => renderRow(idx))}
                            <button
                              type="button"
                              onClick={() => addCriterion(unit, kind)}
                              className={cn(
                                "flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs font-medium",
                                cb.accentBtn(isDarkMode, accent),
                                isMobile && "min-h-[44px] text-sm"
                              )}
                            >
                              <Plus className="h-4 w-4 shrink-0" />
                              {translateUi("Add {{label}} criterion", { label: title })}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {units.length > 0 && (
        <div className={cn("grid gap-2", isMobile ? "grid-cols-1" : "sm:grid-cols-2")}>
          <button
            type="button"
            onClick={() => setIndustryPickerOpen(true)}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-3 text-sm font-medium",
              isDarkMode
                ? "border-indigo-500/50 text-indigo-200 hover:bg-indigo-950/40"
                : "border-indigo-200 text-indigo-700 hover:bg-indigo-50",
              isMobile && "min-h-[48px]"
            )}
          >
            <ClipboardList className="h-4 w-4" />
            {translateUi("Add industry standard units")}
          </button>
          <button
            type="button"
            onClick={addUnit}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-3 text-sm font-medium",
              isDarkMode
                ? "border-slate-600 text-slate-200 hover:bg-slate-800/60"
                : "border-slate-300 text-slate-700 hover:bg-slate-50",
              isMobile && "min-h-[48px]"
            )}
          >
            <Plus className="h-4 w-4" />
            {translateUi("Add blank unit")}
          </button>
        </div>
      )}

      <IndustryUnitPickerDialog
        open={industryPickerOpen}
        units={TRAIN_DRIVER_V1_CYCLE.units as LibraryUnit[]}
        items={items}
        onClose={() => setIndustryPickerOpen(false)}
        onAdd={addIndustryUnits}
        isDarkMode={isDarkMode}
        isMobile={isMobile}
      />
    </div>
  );
}
